import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Swords, X } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import {
  DUEL_FORMATS,
  DUEL_GAME_MODES,
  duelFormatLabelKey,
  duelGameModeLabelKey,
  type DuelFormat,
  type DuelGameMode,
} from '@/types/qduels';
import { cn } from '@/lib/cn';

interface ChallengeOptionsSheetProps {
  /** Render the sheet when true. Parent mounts it only while opening, so the
   *  selectors reset to their defaults (bo3 / REGULAR) on every open. */
  open: boolean;
  opponentName: string;
  /** Disables the submit button while the create-challenge request is in flight. */
  busy: boolean;
  onSubmit: (opts: { format: DuelFormat; gameMode: DuelGameMode }) => void;
  onClose: () => void;
}

// English fallbacks if an i18n key is somehow missing (keys live in
// locales/{it,en}/general.json under social.*).
const FORMAT_FALLBACK: Record<DuelFormat, string> = {
  bo1: 'Single game',
  bo3: 'Best of 3',
  bo5: 'Best of 5',
};
const MODE_FALLBACK: Record<DuelGameMode, string> = {
  REGULAR: 'Classic',
  duel: 'Duel',
  DOUBLE_ELIXIR: 'Double Elixir',
  TRIPLE_ELIXIR: 'Triple Elixir',
  SUDDEN_DEATH: 'Sudden Death',
  RAMP_UP: 'Ramp Up',
  TOUCHDOWN: 'Touchdown',
  HEIST: 'Heist',
};

/**
 * Bottom sheet to pick the game mode + best-of before sending a 1v1 duel.
 * Controlled + self-contained: the parent owns `open`/`busy` and receives the
 * chosen options via `onSubmit`. Defaults: bo3 + REGULAR.
 */
export function ChallengeOptionsSheet({
  open,
  opponentName,
  busy,
  onSubmit,
  onClose,
}: ChallengeOptionsSheetProps) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const [format, setFormat] = useState<DuelFormat>('bo3');
  const [gameMode, setGameMode] = useState<DuelGameMode>('REGULAR');

  if (!open) return null;

  const pickFormat = (f: DuelFormat) => {
    haptics.selection();
    setFormat(f);
  };
  const pickMode = (m: DuelGameMode) => {
    haptics.selection();
    setGameMode(m);
  };
  const submit = () => {
    if (busy) return;
    haptics.impact('medium');
    onSubmit({ format, gameMode });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label={t('social.duel_cancel', { defaultValue: 'Cancel' })}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative w-full max-w-md rounded-t-2xl bg-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-bold text-fg">
            <Swords className="h-4 w-4 text-brand" />
            {t('social.duel_choose_options', { defaultValue: 'Choose options' })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('social.duel_cancel', { defaultValue: 'Cancel' })}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-tertiary text-fg-muted active:opacity-80"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-4 truncate text-sm text-fg-muted">{opponentName}</p>

        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
          {t('social.duel_best_of', { defaultValue: 'Best of' })}
        </p>
        <div className="mb-4 flex gap-2">
          {DUEL_FORMATS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => pickFormat(f)}
              aria-pressed={format === f}
              className={cn(
                'flex-1 rounded-xl px-3 py-2 text-sm font-bold transition-colors',
                format === f ? 'bg-brand text-brand-fg' : 'bg-bg-tertiary text-fg-muted',
              )}
            >
              {t(duelFormatLabelKey(f), { defaultValue: FORMAT_FALLBACK[f] })}
            </button>
          ))}
        </div>

        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
          {t('social.duel_game_mode', { defaultValue: 'Game mode' })}
        </p>
        <div className="mb-5 flex flex-wrap gap-2">
          {DUEL_GAME_MODES.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => pickMode(m)}
              aria-pressed={gameMode === m}
              className={cn(
                'rounded-full px-3 py-1.5 text-xs font-bold transition-colors',
                gameMode === m ? 'bg-brand text-brand-fg' : 'bg-bg-tertiary text-fg-muted',
              )}
            >
              {t(duelGameModeLabelKey(m), { defaultValue: MODE_FALLBACK[m] })}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-50"
        >
          <Swords className="h-4 w-4" />
          {t('social.duel_send', { defaultValue: 'Send challenge' })}
        </button>
      </div>
    </div>
  );
}
